import type { JSX, ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';
import { AppProvider } from './context/AppContext';
import { useApp } from './hooks/useApp';
import { SessionTabsProvider } from './hooks/useSessionTabs';
import { DebugPanelProvider } from './contexts/DebugPanelContext';
import { HelpOverlayProvider } from './contexts/HelpOverlayContext';
import { useGlobalKeyboardShortcuts } from './hooks/useGlobalKeyboardShortcuts';
import AppShell from './components/layout/AppShell';
import ProtectedRoute from './components/layout/ProtectedRoute';
import ParallelCalcDebugPanel from './components/admin/ParallelCalcDebugPanel';
import HelpOverlay from './components/help/HelpOverlay';
import { PostHogIdentifyBridge } from './components/observability/PostHogIdentifyBridge';
import Dashboard from './pages/Dashboard';
import Files from './pages/Files';
import Settings from './pages/Settings';
import BuyingSession from './pages/BuyingSession';
import Tasks from './pages/Tasks';
import Placeholder from './pages/Placeholder';
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import CatalogProducts from './pages/catalog/Products';
import CatalogFlagged from './pages/catalog/Flagged';
import CatalogIntakeResolution from './pages/catalog/IntakeResolution';
import ProductDetail from './pages/catalog/ProductDetail';
import POBuilder from './pages/purchasing/POBuilder';
import PODetail from './pages/purchasing/PODetail';
import PurchaseOrders from './pages/purchasing/PurchaseOrders';
import Receiving from './pages/purchasing/Receiving';
import ReceivingDetail from './pages/purchasing/ReceivingDetail';
import Discrepancies from './pages/purchasing/Discrepancies';
import DiscrepancyDetail from './pages/purchasing/DiscrepancyDetail';
import Inventory from './pages/inventory/Inventory';
import Restock from './pages/inventory/Restock';
import Listings from './pages/listings/Listings';
import Directory from './pages/directory/Directory';
import ReturnsInspection from './pages/returns/ReturnsInspection';
import ReturnsDisposition from './pages/returns/ReturnsDisposition';
import ReturnsDiscrepancies from './pages/returns/ReturnsDiscrepancies';
import TransfersInbound from './pages/transfers/TransfersInbound';
import TransfersRemovals from './pages/transfers/TransfersRemovals';
import FulfillmentOrders from './pages/fulfillment/FulfillmentOrders';
import FulfillmentOrderDetail from './pages/fulfillment/FulfillmentOrderDetail';
import FulfillmentChannelFulfilled from './pages/fulfillment/FulfillmentChannelFulfilled';
import FulfillmentReview from './pages/fulfillment/FulfillmentReview';
import FulfillmentVerify from './pages/fulfillment/FulfillmentVerify';
import FulfillmentFulfill from './pages/fulfillment/FulfillmentFulfill';
import FulfillmentPick from './pages/fulfillment/FulfillmentPick';
import FulfillmentShip from './pages/fulfillment/FulfillmentShip';
import FulfillmentAwaitingLabel from './pages/fulfillment/FulfillmentAwaitingLabel';
import FulfillmentAwaitingPickup from './pages/fulfillment/FulfillmentAwaitingPickup';
import FulfillmentPTO from './pages/fulfillment/FulfillmentPTO';
import FulfillmentOnHold from './pages/fulfillment/FulfillmentOnHold';
import FulfillmentFollowups from './pages/fulfillment/FulfillmentFollowups';
import FulfillmentDiscrepancies from './pages/fulfillment/FulfillmentDiscrepancies';
import Reports from './pages/reports/Reports';
import FinanceCreditCash from './pages/finance/FinanceCreditCash';
import FinanceInvoices from './pages/finance/FinanceInvoices';
import FinancePayouts from './pages/finance/FinancePayouts';
import FinanceStub from './pages/finance/FinanceStub';

interface AuthGuardProps {
  children: ReactNode;
}

function AuthGuard({ children }: AuthGuardProps): JSX.Element {
  const { user, loading } = useApp();

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-0 flex items-center justify-center">
        <div className="text-ink-muted text-sm">Loading…</div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

/** Wrapper that mounts the global keyboard-shortcut listener inside the
 *  AppContext + DebugPanel context tree. Must be a child of both providers
 *  because useGlobalKeyboardShortcuts reads useApp() and useDebugPanel(). */
function GlobalShortcutsBridge({ children }: { children: ReactNode }): JSX.Element {
  useGlobalKeyboardShortcuts();
  return <>{children}</>;
}

function AppRoutes(): JSX.Element {
  const { user } = useApp();

  return (
    <SessionTabsProvider>
      <Routes>
        <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <Login />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        <Route
          element={
            <AuthGuard>
              <AppShell />
            </AuthGuard>
          }
        >
          {/* Root redirect */}
          <Route index element={<Navigate to="/dashboard" replace />} />

          {/* Single-page domains */}
          <Route
            path="dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="tasks"
            element={
              <ProtectedRoute>
                <Tasks />
              </ProtectedRoute>
            }
          />
          <Route
            path="files"
            element={
              <ProtectedRoute>
                <Files />
              </ProtectedRoute>
            }
          />

          {/* Catalog */}
          <Route
            path="catalog/products"
            element={
              <ProtectedRoute>
                <CatalogProducts />
              </ProtectedRoute>
            }
          />
          <Route
            path="catalog/products/:isgSku"
            element={
              <ProtectedRoute>
                <ProductDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="catalog/flagged"
            element={
              <ProtectedRoute>
                <CatalogFlagged />
              </ProtectedRoute>
            }
          />
          <Route
            path="catalog/flagged/:isgSku"
            element={
              <ProtectedRoute>
                <ProductDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="catalog/intake"
            element={
              <ProtectedRoute>
                <CatalogIntakeResolution />
              </ProtectedRoute>
            }
          />

          {/* Purchasing — landing: Purchasing Pipeline; Buying Session wired to Day 4 engine */}
          <Route
            path="purchasing"
            element={
              <ProtectedRoute>
                <Placeholder />
              </ProtectedRoute>
            }
          />
          <Route
            path="purchasing/buying-session"
            element={
              <ProtectedRoute>
                <BuyingSession />
              </ProtectedRoute>
            }
          />
          <Route
            path="purchasing/po-builder"
            element={
              <ProtectedRoute>
                <POBuilder />
              </ProtectedRoute>
            }
          />
          <Route
            path="purchasing/purchase-orders"
            element={
              <ProtectedRoute>
                <PurchaseOrders />
              </ProtectedRoute>
            }
          />
          <Route
            path="purchasing/purchase-orders/:poId"
            element={
              <ProtectedRoute>
                <PODetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="purchasing/purchase-orders/:poId/edit"
            element={
              <ProtectedRoute>
                <POBuilder />
              </ProtectedRoute>
            }
          />
          <Route
            path="purchasing/receiving"
            element={
              <ProtectedRoute>
                <Receiving />
              </ProtectedRoute>
            }
          />
          <Route
            path="purchasing/receiving/:poId"
            element={
              <ProtectedRoute>
                <ReceivingDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="purchasing/discrepancies"
            element={
              <ProtectedRoute>
                <Discrepancies />
              </ProtectedRoute>
            }
          />
          <Route
            path="purchasing/discrepancies/:id"
            element={
              <ProtectedRoute>
                <DiscrepancyDetail />
              </ProtectedRoute>
            }
          />

          {/* Inventory — landing: Stock Visibility */}
          <Route
            path="inventory"
            element={
              <ProtectedRoute>
                <Inventory />
              </ProtectedRoute>
            }
          />
          <Route
            path="inventory/restock"
            element={
              <ProtectedRoute>
                <Restock />
              </ProtectedRoute>
            }
          />
          <Route
            path="inventory/reconciliation"
            element={
              <ProtectedRoute>
                <Placeholder />
              </ProtectedRoute>
            }
          />

          {/* Listings — landing: Listings Overview */}
          <Route
            path="listings"
            element={
              <ProtectedRoute>
                <Listings />
              </ProtectedRoute>
            }
          />
          <Route
            path="listings/review"
            element={
              <ProtectedRoute>
                <Placeholder />
              </ProtectedRoute>
            }
          />

          {/* Fulfillment — landing: Orders */}
          <Route path="fulfillment" element={<Navigate to="/fulfillment/orders" replace />} />
          <Route
            path="fulfillment/orders"
            element={
              <ProtectedRoute>
                <FulfillmentOrders />
              </ProtectedRoute>
            }
          />
          <Route
            path="fulfillment/orders/:id"
            element={
              <ProtectedRoute>
                <FulfillmentOrderDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="fulfillment/channel-fulfilled"
            element={
              <ProtectedRoute>
                <FulfillmentChannelFulfilled />
              </ProtectedRoute>
            }
          />
          <Route
            path="fulfillment/review"
            element={
              <ProtectedRoute>
                <FulfillmentReview />
              </ProtectedRoute>
            }
          />
          <Route
            path="fulfillment/verify"
            element={
              <ProtectedRoute>
                <FulfillmentVerify />
              </ProtectedRoute>
            }
          />
          <Route
            path="fulfillment/fulfill"
            element={
              <ProtectedRoute>
                <FulfillmentFulfill />
              </ProtectedRoute>
            }
          />
          <Route
            path="fulfillment/pick"
            element={
              <ProtectedRoute>
                <FulfillmentPick />
              </ProtectedRoute>
            }
          />
          <Route
            path="fulfillment/ship"
            element={
              <ProtectedRoute>
                <FulfillmentShip />
              </ProtectedRoute>
            }
          />
          <Route
            path="fulfillment/awaiting-label"
            element={
              <ProtectedRoute>
                <FulfillmentAwaitingLabel />
              </ProtectedRoute>
            }
          />
          <Route
            path="fulfillment/awaiting-pickup"
            element={
              <ProtectedRoute>
                <FulfillmentAwaitingPickup />
              </ProtectedRoute>
            }
          />
          <Route
            path="fulfillment/pto"
            element={
              <ProtectedRoute>
                <FulfillmentPTO />
              </ProtectedRoute>
            }
          />
          <Route
            path="fulfillment/on-hold"
            element={
              <ProtectedRoute>
                <FulfillmentOnHold />
              </ProtectedRoute>
            }
          />
          <Route
            path="fulfillment/followups"
            element={
              <ProtectedRoute>
                <FulfillmentFollowups />
              </ProtectedRoute>
            }
          />
          <Route
            path="fulfillment/discrepancies"
            element={
              <ProtectedRoute>
                <FulfillmentDiscrepancies />
              </ProtectedRoute>
            }
          />

          {/* Returns — landing: Returns Pipeline */}
          <Route
            path="returns"
            element={
              <ProtectedRoute>
                <ReturnsInspection />
              </ProtectedRoute>
            }
          />
          <Route
            path="returns/inspection"
            element={
              <ProtectedRoute>
                <ReturnsInspection />
              </ProtectedRoute>
            }
          />
          <Route
            path="returns/disposition"
            element={
              <ProtectedRoute>
                <ReturnsDisposition />
              </ProtectedRoute>
            }
          />
          <Route
            path="returns/discrepancies"
            element={
              <ProtectedRoute>
                <ReturnsDiscrepancies />
              </ProtectedRoute>
            }
          />

          {/* Transfers — no landing */}
          <Route
            path="transfers/inbound"
            element={
              <ProtectedRoute>
                <TransfersInbound />
              </ProtectedRoute>
            }
          />
          <Route
            path="transfers/removals"
            element={
              <ProtectedRoute>
                <TransfersRemovals />
              </ProtectedRoute>
            }
          />

          {/* Finance — no landing (Admin only) */}
          <Route path="finance" element={<Navigate to="/finance/credit-cash" replace />} />
          <Route
            path="finance/credit-cash"
            element={
              <ProtectedRoute>
                <FinanceCreditCash />
              </ProtectedRoute>
            }
          />
          <Route
            path="finance/invoices"
            element={
              <ProtectedRoute>
                <FinanceInvoices />
              </ProtectedRoute>
            }
          />
          <Route
            path="finance/payouts"
            element={
              <ProtectedRoute>
                <FinancePayouts />
              </ProtectedRoute>
            }
          />
          <Route
            path="finance/reconciliation"
            element={
              <ProtectedRoute>
                <FinanceStub path="/finance/reconciliation" />
              </ProtectedRoute>
            }
          />
          <Route
            path="finance/expenses"
            element={
              <ProtectedRoute>
                <FinanceStub path="/finance/expenses" />
              </ProtectedRoute>
            }
          />
          <Route
            path="finance/payroll"
            element={
              <ProtectedRoute>
                <FinanceStub path="/finance/payroll" />
              </ProtectedRoute>
            }
          />
          <Route
            path="finance/review"
            element={
              <ProtectedRoute>
                <FinanceStub path="/finance/review" />
              </ProtectedRoute>
            }
          />

          {/* Reports — in-page left panel (Admin only) */}
          <Route
            path="reports"
            element={
              <ProtectedRoute>
                <Reports />
              </ProtectedRoute>
            }
          />

          {/* Directory — no landing */}
          <Route
            path="directory/distributors"
            element={
              <ProtectedRoute>
                <Directory />
              </ProtectedRoute>
            }
          />
          <Route
            path="directory/manufacturers"
            element={
              <ProtectedRoute>
                <Directory />
              </ProtectedRoute>
            }
          />
          <Route
            path="directory/brands"
            element={
              <ProtectedRoute>
                <Directory />
              </ProtectedRoute>
            }
          />
          <Route
            path="directory/categories"
            element={
              <ProtectedRoute>
                <Directory />
              </ProtectedRoute>
            }
          />
          <Route
            path="directory/marketplaces"
            element={
              <ProtectedRoute>
                <Directory />
              </ProtectedRoute>
            }
          />
          <Route
            path="directory/warehouses"
            element={
              <ProtectedRoute>
                <Directory />
              </ProtectedRoute>
            }
          />

          {/* Settings — in-page left panel; Params & Defaults wired to Day 3 build */}
          <Route
            path="settings"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />
          <Route
            path="settings/*"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />
        </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </SessionTabsProvider>
  );
}

export default function App(): JSX.Element {
  return (
    <BrowserRouter>
      <AppProvider>
        <PostHogIdentifyBridge />
        <DebugPanelProvider>
          <HelpOverlayProvider>
            <GlobalShortcutsBridge>
              <AppRoutes />
              <Analytics />
              {/* Parallel Calculation Debug Panel — admin-only overlay, mounted
                  at app root so Shift+Ctrl+D can open it from any page. The
                  component itself returns null for non-admins (Layer 2 guard). */}
              <ParallelCalcDebugPanel />
              {/* Help overlay — 320px slide-in from right (Day 60c). Opened
                  from the Sidebar Help button; persists open across nav. */}
              <HelpOverlay />
            </GlobalShortcutsBridge>
          </HelpOverlayProvider>
        </DebugPanelProvider>
      </AppProvider>
    </BrowserRouter>
  );
}
